import { createFileRoute } from '@tanstack/react-router'
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import Layout from '@/components/Layout';
import { useI18n } from '@/hooks/useI18n';
import { apiFetch } from '@/utils/api';
import { formatCurrency } from '@/utils/formatters';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
    ArrowLeftIcon,
    PencilIcon,
    BuildingStorefrontIcon,
    CurrencyDollarIcon,
    TagIcon,
    CalendarIcon,
    CheckCircleIcon,
    XCircleIcon
} from '@heroicons/react/24/outline';
import { Loader2 } from 'lucide-react';

interface MenuItem {
    id: string;
    name: string;
    description?: string;
    base_price?: number;
    base_cost_price?: number;
    is_active: boolean;
    fruit_master_id?: string;
    fruit_master_name?: string;
    category_id?: string;
    created_at: string;
    updated_at?: string;
}

interface MenuItemLocation {
    id: string;
    cost_center_id: string;
    cost_center_name?: string;
    price?: number;
    cost_price?: number;
    is_available: boolean;
    vat_eat_in?: number;
    vat_take_away?: number;
    size_name?: string;
    inactive: boolean;
    fruit_item_id?: string;
    fruit_location_id?: string;
}

export default function MenuItemDetailPage() {
    const { t } = useI18n();
    const navigate = useNavigate();
  const params = useParams({ strict: false });
  const routerState = useRouterState();
    const { id } = params;

    // State
    const [menuItem, setMenuItem] = useState<MenuItem | null>(null);
    const [locationVariants, setLocationVariants] = useState<MenuItemLocation[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Fetch menu item details
    const fetchMenuItemDetails = async () => {
        if (!id || typeof id !== 'string') return;

        try {
            setLoading(true);
            setError(null);

            const response = await apiFetch(`/api/menu-items/${id}`);

            if (!response.ok) {
                if (response.status === 404) {
                    throw new Error('Menu item not found');
                }
                throw new Error('Failed to fetch menu item details');
            }

            const data = await response.json();
            setMenuItem(data);
            setLocationVariants(data.location_variants || []);

        } catch (err) {
            console.error('Error fetching menu item details:', err);
            setError(err instanceof Error ? err.message : 'Failed to load menu item');
        } finally {
            setLoading(false);
        }
    };

    // Effects
    useEffect(() => {
        fetchMenuItemDetails();
    }, [id]);

    // Handlers
    const handleBack = () => {
        navigate({ to: '/menus'});
    };

    const handleEdit = () => {
        navigate({ to: `/menus/${id}/edit`});
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="h-8 w-8 animate-spin" />
                    <span className="ml-2">Loading menu item...</span>
                </div>
            </Layout>
        );
    }

    if (error || !menuItem) {
        return (
            <Layout>
                <div className="space-y-6">
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" onClick={handleBack} className="flex items-center gap-2">
                            <ArrowLeftIcon className="h-4 w-4" />
                            Back to Menu Items
                        </Button>
                    </div>

                    <Card>
                        <CardContent className="p-6 text-center">
                            <div className="text-red-600 mb-4">
                                <XCircleIcon className="h-12 w-12 mx-auto mb-2" />
                                <p className="text-lg font-semibold">Error</p>
                                <p>{error || 'Menu item not found'}</p>
                            </div>
                            <Button onClick={fetchMenuItemDetails}>
                                Try Again
                            </Button>
                        </CardContent>
                    </Card>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" onClick={handleBack} className="flex items-center gap-2">
                            <ArrowLeftIcon className="h-4 w-4" />
                            Back to Menu Items
                        </Button>
                        <div>
                            <h1 className="text-3xl font-bold tracking-tight">
                                {menuItem.name}
                            </h1>
                            <p className="text-muted-foreground">
                                Menu item details and location variants
                            </p>
                        </div>
                    </div>
                    <Button onClick={handleEdit} className="flex items-center gap-2">
                        <PencilIcon className="h-4 w-4" />
                        Edit Item
                    </Button>
                </div>

                {/* Main Details */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Basic Information */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <TagIcon className="h-5 w-5" />
                                Basic Information
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div>
                                <label className="text-sm font-medium text-muted-foreground">Name</label>
                                <p className="text-lg font-semibold">{menuItem.name}</p>
                            </div>

                            {menuItem.description && (
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground">Description</label>
                                    <p>{menuItem.description}</p>
                                </div>
                            )}

                            <div>
                                <label className="text-sm font-medium text-muted-foreground">Status</label>
                                <div className="flex items-center gap-2 mt-1">
                                    <Badge variant={menuItem.is_active ? "default" : "secondary"}>
                                        {menuItem.is_active ? (
                                            <>
                                                <CheckCircleIcon className="h-3 w-3 mr-1" />
                                                Active
                                            </>
                                        ) : (
                                            <>
                                                <XCircleIcon className="h-3 w-3 mr-1" />
                                                Inactive
                                            </>
                                        )}
                                    </Badge>
                                </div>
                            </div>

                            <Separator />

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground">Created</label>
                                    <p className="text-sm">
                                        {new Date(menuItem.created_at).toLocaleDateString()}
                                    </p>
                                </div>
                                {menuItem.updated_at && (
                                    <div>
                                        <label className="text-sm font-medium text-muted-foreground">Updated</label>
                                        <p className="text-sm">
                                            {new Date(menuItem.updated_at).toLocaleDateString()}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Pricing Information */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <CurrencyDollarIcon className="h-5 w-5" />
                                Pricing Information
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground">Base Price</label>
                                    <p className="text-2xl font-bold text-green-600">
                                        {menuItem.base_price ? formatCurrency(menuItem.base_price) : 'Not set'}
                                    </p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground">Base Cost</label>
                                    <p className="text-2xl font-bold text-orange-600">
                                        {menuItem.base_cost_price ? formatCurrency(menuItem.base_cost_price) : 'Not set'}
                                    </p>
                                </div>
                            </div>

                            {menuItem.base_price && menuItem.base_cost_price && (
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground">Profit Margin</label>
                                    <p className="text-lg font-semibold text-blue-600">
                                        {formatCurrency(menuItem.base_price - menuItem.base_cost_price)}
                                        <span className="text-sm text-muted-foreground ml-2">
                                            ({Math.round(((menuItem.base_price - menuItem.base_cost_price) / menuItem.base_price) * 100)}%)
                                        </span>
                                    </p>
                                </div>
                            )}

                            {menuItem.fruit_master_id && (
                                <>
                                    <Separator />
                                    <div>
                                        <label className="text-sm font-medium text-muted-foreground">Fruit BI Integration</label>
                                        <div className="mt-1 space-y-1">
                                            <p className="text-sm font-mono bg-muted px-2 py-1 rounded">
                                                ID: {menuItem.fruit_master_id}
                                            </p>
                                            {menuItem.fruit_master_name && (
                                                <p className="text-sm">
                                                    Name: {menuItem.fruit_master_name}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Location Variants */}
                {locationVariants.length > 0 && (
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <BuildingStorefrontIcon className="h-5 w-5" />
                                Location Variants ({locationVariants.length})
                            </CardTitle>
                            <CardDescription>
                                Location-specific pricing and availability for this menu item
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {locationVariants.map((variant) => (
                                    <Card key={variant.id} className="border-l-4 border-l-blue-500">
                                        <CardContent className="p-4">
                                            <div className="space-y-2">
                                                <div className="flex items-center justify-between">
                                                    <h4 className="font-semibold">
                                                        {variant.cost_center_name || `Location ${variant.cost_center_id.slice(0, 8)}`}
                                                    </h4>
                                                    <Badge variant={variant.is_available && !variant.inactive ? "default" : "secondary"}>
                                                        {variant.is_available && !variant.inactive ? 'Available' : 'Unavailable'}
                                                    </Badge>
                                                </div>

                                                {variant.price && (
                                                    <div>
                                                        <span className="text-sm text-muted-foreground">Price: </span>
                                                        <span className="font-semibold text-green-600">
                                                            {formatCurrency(variant.price)}
                                                        </span>
                                                    </div>
                                                )}

                                                {variant.cost_price && (
                                                    <div>
                                                        <span className="text-sm text-muted-foreground">Cost: </span>
                                                        <span className="font-semibold text-orange-600">
                                                            {formatCurrency(variant.cost_price)}
                                                        </span>
                                                    </div>
                                                )}

                                                {variant.size_name && (
                                                    <div>
                                                        <span className="text-sm text-muted-foreground">Size: </span>
                                                        <span className="text-sm">{variant.size_name}</span>
                                                    </div>
                                                )}

                                                {variant.fruit_item_id && (
                                                    <div>
                                                        <span className="text-sm text-muted-foreground">Fruit ID: </span>
                                                        <span className="text-xs font-mono bg-muted px-1 py-0.5 rounded">
                                                            {variant.fruit_item_id}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* No Location Variants Message */}
                {locationVariants.length === 0 && (
                    <Card>
                        <CardContent className="p-6 text-center text-muted-foreground">
                            <BuildingStorefrontIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                            <p>No location variants found for this menu item.</p>
                            <p className="text-sm">This item uses base pricing across all locations.</p>
                        </CardContent>
                    </Card>
                )}
            </div>
        </Layout>
    );
}

export const Route = createFileRoute('/menus/$id')({
  component: MenuItemDetailPage,
})
